import Actions from './PlayerActions'
import Landmark from './cards/Landmark'
import {Color} from './cards/Card'

export default class Player {
	constructor({
		landmarks = require('../config/landmarks.json'),
		actions = Actions
	}) {
		this.cards = []
		this.coins = 0
		this.landmarks = {}
		this.actions = new actions
		if(!(this.actions instanceof Actions))
			throw Error('Player must have callable actions')

		// make the landmarks
		for(let cardName of Object.keys(landmarks)) {
			let data = landmarks[cardName]
			data.name = cardName

			this.landmarks[cardName] = new Landmark(data)
		}
	}

	/**
	 * Alias to Card's setOwner
	 * @param {Card} card
	 * @return {Player}
	 */
	addCard(card) {
		card.setOwner(this)
		return this
	}

	/**
	 * A part of a game
	 * Set Game listeners here
	 * @param game
	 */
	setGame(game) {
		this.game = game
		//this.game.on('turn')
	}

	/**
	 * Buys a landmark if it is available
	 * @param name
	 * @return {boolean}
	 */
	buyLandmark(name) {
		if(this.landmarks[name] && this.coins >= this.landmarks[name].cost) {
			this.coins -= this.landmarks[name].cost
			this.landmarks[name].activate()
			return true
		}

		return false
	}

	/**
	 * Buys a card if possible
	 * @param card
	 * @return boolean
	 */
	buyCard(card) {
		if(this.coins >= card.cost) {
			this.coins -= card.cost
			this.addCard(card)
			return true
		}

		return false
	}

	/**
	 * Check if player is a winner
	 * @return {boolean}
	 */
	won() {
		for(let name of Object.keys(this.landmarks))
			if(!this.landmarks[name].activated)
				return false
		return true
	}

	/**
	 * Can increase roll by 2
	 * @param {number[]} dice
	 * @return {boolean}
	 */
	canAdd2ToRoll(dice) {
		if(this.landmarks['Harbor'].activated)
			if(dice.length >= 2 && dice[0] + dice[1] >= 10)
				return true
		return false
	}

	/**
	 * Can re roll dice
	 * @return {boolean}
	 */
	canRollAgain() {
		return this.landmarks['Radio Tower'].activated;
	}

	/**
	 * Can take another turn
	 * @param {number[]} dice
	 * @return {boolean}
	 */
	canTurnAgain(dice) {
		if(this.landmarks['Amusement Park'].activated)
			if(dice.length === 2 && dice[0] === dice[1])
				return true
		return false
	}

	/**
	 * Can roll 2 dice
	 * @return {boolean}
	 */
	canRoll2Dice() {
		return this.landmarks['Train Station'].activated;
	}

	/**
	 * Can buy the landmarks
	 * @param {Landmark[]} landmarks
	 * @return {boolean}
	 */
	canBuyLandmarks(landmarks) {
		const possible = this.possibleLandmarks()

		// stop if landmark isn't possible
		if(!landmarks.every(lm => possible.indexOf(lm) !== -1))
			return false
		
		const totalCost = landmarks.reduce((acc, lm) => acc + lm.cost, 0)
		return totalCost <= this.coins
	}

	/**
	 * Can buy the cards
	 * @param {Card[]} cards
	 * @return {boolean}
	 */
	canBuyCards(cards) {
		const notOwn = this.cards
			.filter(card => card.color === Color.PURPLE)
			.every(card => this.cards.indexOf(card.name) === -1)

		// owns a matching purple
		if(!notOwn)
			return false
		
		const totalCost = cards.reduce((acc, card) => acc + card.cost, 0)
		return totalCost <= this.coins
	}

	/**
	 * The possible landmarks player can buy
	 * @return {Landmark[]}
	 */
	possibleLandmarks() {
		return Object.keys(this.landmarks)
			.map(name => this.landmarks[name])
			.filter(lm => !lm.activated)
			.filter(lm => this.coins >= lm.cost)
	}

	/**
	 * The indies of the possible cards on the market player can buy
	 * @param {Card[]} market
	 * @return {number[]} Indexes of possible cards in the market
	 */
	possibleCards(market) {
		const indexes = market.map((v, index) => index)

		return indexes
			.filter(i => this.coins >= market[i].cost)
			// keep purples I don't own
			.filter(i => market[i].color !== Color.PURPLE ||
				this.cards
					.filter(c => c.color === Color.PURPLE)
					.map(c => c.name)
					.indexOf(market[i].name) === -1
			)
			// remove duplicate purples
			.filter(i => market[i].color !== Color.PURPLE ||
				market
					.map(c => c.name)
					.indexOf(market[i].name) === i
			)
	}
}